/**
 * FilterBadge — header filter affordance shared by the Pulse and OEM dashboards.
 * Mirrors the gripp-os dashboard FilterPanel button: a filter icon carrying a
 * small count badge of the active dashboard filters (activity-type + group +
 * label + person facets). Pressing it opens the full-overlay filter panel;
 * clearing happens inside the panel via its reset control.
 *
 * Web-only — do NOT import from mobile code.
 */
import { FilterIcon, tokens } from '@gripp/shared-ui';
import { useState } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';

export const FilterBadge = ({
  count,
  onPress,
  label,
  testID = 'home-dashboard-filter-badge',
  countTestID = 'home-dashboard-filter-count',
}: {
  count: number;
  onPress: () => void;
  label: string;
  testID?: string;
  countTestID?: string;
}) => {
  const [hovered, setHovered] = useState(false);
  const active = count > 0;
  return (
    <Pressable
      style={[styles.filterButton, hovered && styles.filterButtonHovered]}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={active ? `${label} (${count})` : label}
      testID={testID}
    >
      <FilterIcon
        size={20}
        color={active ? tokens.colors.neutral[800] : tokens.colors.slate[400]}
      />
      {active && (
        <View style={styles.filterCount} testID={countTestID}>
          <Text style={styles.filterCountText}>{count}</Text>
        </View>
      )}
    </Pressable>
  );
};

const styles = StyleSheet.create({
  filterButton: {
    width: 36,
    height: 36,
    borderRadius: tokens.borderRadius.full,
    alignItems: 'center',
    justifyContent: 'center',
  },
  filterButtonHovered: {
    backgroundColor: tokens.colors.neutral[200],
  },
  // Count pill overlaid on the filter icon's top-right (design: brand-orange
  // badge with the active-filter count).
  filterCount: {
    position: 'absolute',
    top: 2,
    right: 2,
    minWidth: 16,
    height: 16,
    paddingHorizontal: 4,
    borderRadius: tokens.borderRadius.full,
    backgroundColor: tokens.colors.brand.core,
    alignItems: 'center',
    justifyContent: 'center',
  },
  filterCountText: {
    ...tokens.typography.label.small,
    fontSize: 10,
    lineHeight: 14,
    fontWeight: '700',
    color: tokens.colors.brand.white,
    fontVariant: ['tabular-nums'],
  },
});
